import React, {
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import {
  darkTheme,
  radius,
  spacing,
  typographyTokens
} from "@yleisradio/yds-core";
import styled from "styled-components";

import {
  RETRY_TIME_BEFORE_TIMER_FAILS_IN_SECONDS,
  SHOW_TIMER_BEFORE_START_IN_SECONDS
} from "../../constants";
import { useMediaContext } from "../../contexts/MediaContext";
import { secondsToFormattedTimeUnits } from "../../helpers/timeHelpers";
import { videoFontColorOnScreen } from "../../styles/variables";
import { FadingComponent } from "../FadingComponent";

export enum TimerState {
  Pending,
  Running,
  Completed,
  OnOverTime,
  Failed
}

const COUNTDOWN_WIDTH_PX = 116;
const UNIT_BLOCK_MARGIN = 4;
const UNIT_BLOCK_WIDTH_PX = COUNTDOWN_WIDTH_PX / 2 - UNIT_BLOCK_MARGIN / 2;

const TimerContainer = styled.div`
  color: ${videoFontColorOnScreen};
  margin-top: ${spacing.SPACING_12};
  text-align: center;
  width: 100%;
`;

const UnitBlock = styled.div`
  background-color: ${darkTheme.BACKGROUND_DIALOG_OVERLAY};
  border-radius: ${radius.LARGE};
  color: ${darkTheme.TEXT_DEFAULT};
  display: inline-block;
  font-weight: normal;
  padding: ${spacing.SPACING_8} 0 ${spacing.SPACING_4};
  text-align: center;
  width: ${UNIT_BLOCK_WIDTH_PX}px;
`;

const TimerCounter = styled.div`
  width: ${COUNTDOWN_WIDTH_PX}px;

  ${UnitBlock}:first-child {
    margin-right: ${spacing.SPACING_4};
  }
`;

const Number = styled.span`
  display: block;
  font-size: ${typographyTokens.FONT_SIZE_3XL};
  font-variant: tabular-nums;
`;

const UnitLabel = styled.span`
  display: block;
  font-size: ${typographyTokens.FONT_SIZE_SM};
  margin-top: ${spacing.SPACING_4};
`;

const getTimeRemaining = (date: Date): number =>
  Math.ceil((date.valueOf() - new Date().valueOf()) / 1000);

const TimeUnits = ({
  minutes,
  seconds
}: {
  minutes: string;
  seconds: string;
}) => (
  <TimerCounter>
    <UnitBlock>
      <Number>{minutes}</Number>
      <UnitLabel>min</UnitLabel>
    </UnitBlock>
    <UnitBlock>
      <Number>{seconds}</Number>
      <UnitLabel>s</UnitLabel>
    </UnitBlock>
  </TimerCounter>
);

const Timer = ({ timeRemaining }: { timeRemaining: number }) => (
  <TimerContainer>
    <TimeUnits {...{ ...secondsToFormattedTimeUnits(timeRemaining) }} />
  </TimerContainer>
);

const resolveTimerState = (timeRemaining: number): TimerState => {
  if (timeRemaining >= SHOW_TIMER_BEFORE_START_IN_SECONDS) {
    return TimerState.Pending;
  }
  if (timeRemaining < SHOW_TIMER_BEFORE_START_IN_SECONDS && timeRemaining > 0) {
    return TimerState.Running;
  }
  if (timeRemaining === 0) {
    return TimerState.Completed;
  }
  if (timeRemaining > -RETRY_TIME_BEFORE_TIMER_FAILS_IN_SECONDS) {
    return TimerState.OnOverTime;
  }
  return TimerState.Failed;
};

interface Props extends PropsWithChildren {
  onTimerEnd: () => void;
  onTimerStateChange: Dispatch<SetStateAction<TimerState>>;
}

export const CountDownTimer: FC<Props> = ({
  onTimerEnd,
  onTimerStateChange
}) => {
  const data = useMediaContext();

  const [startDate, setStartDate] = useState<Date>();
  const [previousTimerState, setPreviousTimerState] = useState<TimerState>();
  const [timerState, setTimerState] = useState<TimerState>(TimerState.Pending);
  const [timeRemaining, setTimeRemaining] = useState<number>();
  const timerId = useRef<number>(undefined);

  const setTimer = (callBack: () => void, interval: number) => {
    timerId.current = window.setTimeout(callBack, interval);
  };

  const resetTimer = useCallback(() => {
    window.clearTimeout(timerId.current);
  }, []);

  const update = useCallback(() => {
    if (!startDate) return;
    const newTimeRemaining = getTimeRemaining(startDate);
    const newTimerState = resolveTimerState(newTimeRemaining);
    setTimeRemaining(newTimeRemaining);
    setPreviousTimerState(timerState);
    if (newTimerState !== timerState) {
      setTimerState(newTimerState);
    }
  }, [startDate, timerState]);

  const resolveTimer = useCallback(() => {
    resetTimer();

    if (!startDate) return;

    if (
      timerState === TimerState.Pending ||
      timerState === TimerState.Running ||
      timerState === TimerState.Completed
    ) {
      setTimer(update, 1000);
    } else if (
      previousTimerState === TimerState.Completed &&
      timerState === TimerState.OnOverTime
    ) {
      onTimerEnd();
      setTimer(update, 1000);
    } else if (timerState === TimerState.OnOverTime) {
      setTimer(() => {
        update();
        onTimerEnd();
      }, 10000);
    }
  }, [
    onTimerEnd,
    previousTimerState,
    resetTimer,
    startDate,
    timerState,
    update
  ]);

  useEffect(() => {
    resolveTimer();
  }, [resolveTimer, timeRemaining]);

  useEffect(() => {
    onTimerStateChange(timerState);
  }, [onTimerStateChange, timerState]);

  useEffect(() => {
    update();
  }, [startDate, update]);

  useEffect(() => setStartDate(new Date(data.startTime || "")), [data]);

  useEffect(() => {
    return () => {
      resetTimer();
    };
  }, [resetTimer]);

  return (
    <FadingComponent
      isVisible={
        timerState === TimerState.Running || timerState === TimerState.Completed
      }>
      <Timer timeRemaining={Math.max(timeRemaining ?? 0, 0)} />
    </FadingComponent>
  );
};
